import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/ecosystem", label: "Ecosystem" },
  { to: "/philosophy", label: "Philosophy & Opportunity" },
  { to: "/team", label: "Team" },
  { to: "/careers", label: "Careers" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#F9F9F7]/85 border-b border-[#E5E5E0]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src="/brand/folks-logo.png" alt="Folks Funds" className="h-10 lg:h-11 w-auto select-none" draggable="false" />
          <span className="hidden md:block font-mono text-[9px] uppercase tracking-[0.22em] text-[#4A5E51] border-l border-[#E5E5E0] pl-3 leading-[1.4]">AIF Cat II<br />SEBI Reg.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-tight transition-colors link-underline ${
                  isActive ? "text-[#111A15]" : "text-[#4A5E51] hover:text-[#111A15]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-contact-cta"
            className="hidden md:inline-flex items-center gap-1.5 bg-[#1A2F24] text-white px-4 py-2.5 text-[13px] font-medium hover:bg-[#111A15] transition-colors"
          >
            Get in touch <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 grid place-items-center text-[#111A15]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E5E5E0] bg-[#F9F9F7]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[15px] font-medium tracking-tight ${
                    isActive ? "text-[#111A15]" : "text-[#4A5E51]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-contact-cta" className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1A2F24] text-white px-5 py-3 text-sm font-medium">
              Get in touch <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
